/**
 * CalendarSpecialtyRow — the practitioner filter as the Practice calendar draws it with no Devices tab (PROD-3307 Q2):
 * PM `components/blocks/calendar-toolbar.tsx` `showSpecialtyFilterRow` (origin/main 5a49b124), a row under the toolbar
 * with the specialty chips (the DS `PACalendarSpecialtyFilter`, graduated from PM's `SpecialtyFilter`, identical) and,
 * at its right, the ≡ "Provider bulk-select" menu with Select all / Unselect all for the practitioner checkboxes.
 * The PA never has a Devices tab, so this is its shape (PM draws the pill + panel only beside a Devices tab).
 *
 * Same data and handlers as the former "All ▾" menu: the specialty chips toggle / clear the store's selection ("All"
 * clears), Select all / Unselect all drive the practitioner selection, each disabled when it would change nothing.
 */
import { useState } from 'react';
import { Menu } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  PACalendarSpecialtyFilter,
} from '@scripsteam/scrips-signal-ds';

export interface CalendarSpecialtyRowProps {
  /** Chip labels in display order, "All" first. */
  specialties: string[];
  selectedSpecialties: string[];
  onSpecialtyToggle: (specialty: string) => void;
  onSpecialtyClear: () => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  allSelected?: boolean;
  noneSelected?: boolean;
}

export function CalendarSpecialtyRow({
  specialties,
  selectedSpecialties,
  onSpecialtyToggle,
  onSpecialtyClear,
  onSelectAll,
  onUnselectAll,
  allSelected = false,
  noneSelected = false,
}: CalendarSpecialtyRowProps) {
  const [open, setOpen] = useState(false);
  return (
    <div data-slot="pa-calendar-specialty-row" className="bg-surface-card border-b border-border-default px-4 py-2 flex items-center gap-3">
      <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">
        <PACalendarSpecialtyFilter
          specialties={specialties}
          selected={selectedSpecialties}
          onToggle={onSpecialtyToggle}
          onClear={onSpecialtyClear}
        />
      </div>
      {/* Provider bulk-select (Flutter parity: the ≡ at the top right selects or clears the practitioner checkboxes,
          not the specialty chips). */}
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        <AnchoredPopoverTrigger
          render={
            <button
              type="button"
              aria-label="Provider bulk-select"
              aria-expanded={open}
              className="flex-shrink-0 p-2 rounded text-text-muted hover:text-text-primary hover:bg-grey-100"
            />
          }
        >
          <Menu className="h-4 w-4" aria-hidden />
        </AnchoredPopoverTrigger>
        <AnchoredPopoverContent
          align="end"
          side="bottom"
          className="min-w-36 bg-surface-card border border-border-default rounded-lg shadow-lg py-1 text-sm"
        >
          <div role="menu" aria-label="Provider bulk-select">
            <button
              type="button"
              role="menuitem"
              disabled={allSelected}
              onClick={() => {
                onSelectAll();
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-1.5 text-text-primary enabled:hover:bg-grey-100 disabled:text-text-hint disabled:cursor-not-allowed"
            >
              Select all
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={noneSelected}
              onClick={() => {
                onUnselectAll();
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-1.5 text-text-primary enabled:hover:bg-grey-100 disabled:text-text-hint disabled:cursor-not-allowed"
            >
              Unselect all
            </button>
          </div>
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </div>
  );
}
